import * as React from 'react';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import {
  Button,
  Card,
  Col,
  Collapse,
  Divider,
  Drawer,
  Form,
  Input,
  Modal,
  notification,
  Progress,
  Row,
  Select,
  Skeleton,
  Spin,
  Switch,
  Tag,
} from 'antd';
import Upload, { RcFile, UploadChangeParam } from 'antd/lib/upload';
import { UploadFile } from 'antd/lib/upload/interface';
import { CloudUploadOutlined, LoadingOutlined } from '@ant-design/icons';
import { L } from '../../../lib/abpUtility';
import { SubscriptionTypes } from '../../../components/EntityConfigs/subscriptionTypes';
import { FormInstance } from 'antd/lib/form/Form';
import WorkflowView from '../../../components/Controls/Widgets/workflowView';


export interface ICreateTenantProps {
  isMobile: boolean;
  visible: boolean;
  loading: boolean;
  primaryColor: string;
  secondaryColor: string;
  defaultImage: string;
  profileImageID: number;
  tenantStates: any;


  onCreate: (values: any) => void;
  onCancel: () => void;
  uploadProfileImage: (file: any) => any;

}

export interface IItemBaseState {
  imageUrl: string;
  imgLoading: boolean;
  selectedStage: number;
  adminSameAsContact: boolean;
  isCloudDatabase: boolean;
  isCloudFTP: boolean;
  storedPaymentInfo: boolean;
  customTheme: boolean;
  sameAsTenantCompanyInfo: boolean;
  sameAsTenantPrimaryContact: boolean;
  sameAsTenantCommunicationInfo: boolean;
  sameAsTenantPaymentInfo: boolean;
  isDemoAccount: boolean;
}

class CreateTenant extends React.Component<ICreateTenantProps, IItemBaseState> {

  formRef = React.createRef<FormInstance>();

  state =
    {
      isDemoAccount: false,
      sameAsTenantCompanyInfo: false,
      sameAsTenantPrimaryContact: false,
      sameAsTenantCommunicationInfo: false,
      sameAsTenantPaymentInfo: false,
      customTheme: false,
      fieldNameSize: '0.6em',
      isCloudDatabase: true,
      isCloudFTP: true,
      storedPaymentInfo: true,
      imgLoading: false,
      imageUrl: '',
      adminSameAsContact: true,
      selectedStage: 1,
    };

  async uploadImage(file: RcFile) {
    this.setState({ imgLoading: true });
    let result = await this.props.uploadProfileImage(file);
    return result.url;
  }

  async getBase64(img: any, callback: any) {
    const reader = new FileReader();
    reader.addEventListener('load', () => callback(reader.result));
    reader.readAsDataURL(img);
  }

  beforeUpload(file: RcFile, fileList: RcFile[]) {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      notification.open({
        message: L('Uh Oh!'),
        description:
          L('Only JPG and PNG Images Allowed')
      });
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      notification.open({
        message: L('Uh Oh!'),
        description:
          L('Image must be smaller than 2MB')
      });
    }
    return isJpgOrPng && isLt2M;
  }

  handleChange = (info: UploadChangeParam<UploadFile<any>>) => {

    if (info.file.status === 'uploading') {
      this.setState({ imgLoading: true });
      return;
    }
    else {

      if (info.file.originFileObj !== undefined) {
        // Get this url from response in real world.
        this.getBase64(info.file.originFileObj, (imageUrl: any) =>
          this.setState({
            imageUrl,
            imgLoading: false,
          }),
        );
      }

    }

  };

  handleFinish = (values: any) => {
    
    let tenantResult = this.formRef.current?.getFieldValue('name').replace(/ /g, '_');

    var valuesFormatted = {
      isActive: true,
      ...values,
      tenancyName: tenantResult, 
      adminEmailAddress: this.formRef.current?.getFieldValue('subscriberEmail'),
      profileImageID: this.props.profileImageID,
      currentState: this.state.selectedStage,
    };

    this.formRef.current?.resetFields();
    this.setState({ imageUrl: '' });
    this.props.onCreate(valuesFormatted);
  }

  handleCancel = () => {
    this.formRef.current?.resetFields();
    this.setState({ imageUrl: '' })
    this.props.onCancel();
  }

  render() {

    const { visible, isMobile } = this.props;
    const { Panel } = Collapse;
    const defaultImage = (<div> {this.props.defaultImage === '' ? <div><CloudUploadOutlined /><br />{L('Upload')}<br />{L('Photo')}</div> : <img src={this.props.defaultImage} alt="avatar" style={{ width: '100%' }} />} </div>);
    const uploadButton = (<div> {this.state.imgLoading ? <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: this.props.primaryColor }} spin />} /> : defaultImage} </div>);
    const uploadWrapper = (<div> {this.props.loading ? <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: this.props.primaryColor }} spin />} /> : uploadButton} </div>);
    const { Option } = Select;

    const tenantStates = {
      totalCount: 4,
      items:
        [
          {
            id: 1,
            name: 'inactive',
            title: 'Inactive',
            profileImageID: 0,
            displayType: 0,
            forwardText: 'Submit for Approval',
            revertText: '',
            restricted: false,
            allowSkipTo: true,
            allowRevert: true,
            nextActionOnly: true,
            lastActionOnly: false,
            noActions: false,
          },
          {
            id: 2,
            name: 'approval',
            title: 'Approval',
            profileImageID: 0,
            displayType: 0,
            forwardText: 'Approve',
            revertText: 'Deny',
            restricted: false,
            allowSkipTo: true,
            allowRevert: true,
            nextActionOnly: false,
            lastActionOnly: false,
            noActions: false,
          },
          {
            id: 3,
            name: 'onboarding',
            title: 'Onboarding',
            profileImageID: 0,
            displayType: 0,
            forwardText: 'Complete',
            revertText: '',
            restricted: false,
            allowSkipTo: true,
            allowRevert: true,
            nextActionOnly: true,
            lastActionOnly: false,
            noActions: false,
          },
          {
            id: 4,
            name: 'active',
            title: 'Active',
            profileImageID: 0,
            displayType: 0,
            forwardText: 'Suspend',
            revertText: '',
            restricted: false,
            allowSkipTo: true,
            allowRevert: true,
            nextActionOnly: true,
            lastActionOnly: false,
            noActions: false,
          },
        ]
    }

    return (

      <div className="font-face-gm">

        <div>
          <Modal className="font-face-gm"
            open={isMobile === false ? (visible === true ? true : false) : false}
            cancelText={L('Cancel')}
            footer={null}
            width={610}
            onCancel={this.handleCancel}
            title={null}
            closable={this.props.isMobile === true ? true : false}
          >



            {this.props.loading === false ?


              <Form
                ref={this.formRef} style={{ marginTop: '0px', marginBottom: '0px', }}
                name={'createTenantForm'} onFinish={(e) => this.handleFinish(e)}>



                <WorkflowView
                  currentState={0}
                  titleText={L('Create New')}
                  subtitleText={L('Account')}
                  primaryColor={this.props.primaryColor}
                  secondaryColor={this.props.secondaryColor}
                  entityStates={tenantStates}
                  stepMove={(e) => this.setState({ selectedStage: e })} 
                  disableSliderEdit={false}
                  disableActionEdit={false}
                  isMobile={false}
                  loading={false}

                />


                <Card bordered={false} style={{ padding: '0px', marginTop: '0px', marginBottom: '-10px' }}>

                  <Row>


                    <Col span={5} offset={0}>
                      <div>
                        <Form.Item name={'profileImageURL'}  >
                          <Upload
                            action={(file) =>
                              this.uploadImage(file)
                            }
                            listType="picture-card"
                            showUploadList={false}
                            beforeUpload={this.beforeUpload}
                            className="avatar-uploader"
                            onChange={this.handleChange}
                            multiple={false}
                            key={'profileImageURL'} >
                            {this.state.imageUrl !== '' ? <img src={this.state.imageUrl} alt="avatar" style={{ width: '100%' }} /> : uploadWrapper}
                          </Upload></Form.Item></div>

                    </Col>

                    <Col span={17} offset={2}>
                      <div>
                        <div style={{ float: 'left', width: '25%', marginRight: '12px' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: '1.2em', textAlign: 'center', width: '100%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Name')}</Tag>
                        </div>
                        <div style={{ float: 'left', width: '71%' }}>
                          <Form.Item className="font-face-gm" name={'name'} key={'name'} rules={[]}>
                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.props.loading} />
                          </Form.Item>
                        </div>
                      </div>
                      <div>
                        <div style={{ float: 'left', width: '25%', marginRight: '12px' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: '1.2em', textAlign: 'center', width: '100%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Title')}</Tag>
                        </div>
                        <div style={{ float: 'left', width: '71%' }}>
                          <Form.Item className="font-face-gm" name={'title'} key={'title'} rules={[]}>

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.props.loading} />

                          </Form.Item>
                        </div>

                      </div>
                    </Col>

                  </Row>

                  <Collapse accordion={true}
                    key={'Name'}
                    bordered={true}
                    defaultActiveKey={'sunscriptionInfo'}
                    style={{
                      fontSize: '0.8em',
                      backgroundColor: 'transparent',
                      marginBottom: '0px',
                      marginTop: '0px',
                      border: 'none',
                      boxShadow: 'none'
                    }}>

                    <Panel className="font-face-gm"
                      style={{ fontSize: '1.8em' }}
                      header={<div style={{ marginLeft: '25px', fontSize: '0.8em' }}>{L('Subscription')}</div>}
                      key={'sunscriptionInfo'} extra={

                        <Switch
                          style={{ float: 'right' }}
                          key={'isDemoAccount'}
                          onClick={(e) => this.setState({ isDemoAccount: e })}
                          size={'default'}
                          checked={this.state.isDemoAccount}
                          checkedChildren={L('Demo Account')}
                          unCheckedChildren={L('Demo Account')} />

                      }>


                      <div style={{ marginBottom: '40px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Plan')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item initialValue={SubscriptionTypes.businessBasic} style={{ margin: '0' }}
                            name={'type'} key={'type'} rules={[]}  >
                            <Select size="large" key={'subscriptionType'} style={{ width: '100%' }}
                              defaultValue={SubscriptionTypes.businessBasic} disabled={this.state.isDemoAccount}>
                              <Option value={SubscriptionTypes.businessBasic}>{SubscriptionTypes.businessBasic_Text}</Option>
                              <Option value={SubscriptionTypes.businessStandard}>{SubscriptionTypes.businessStandard_Text}</Option>
                              <Option value={SubscriptionTypes.enterpriseAdvanced}>{SubscriptionTypes.enterpriseAdvanced_Text}</Option>
                              <Option value={SubscriptionTypes.enterpriseUnlimited}>{SubscriptionTypes.enterpriseUnlimited_Text}</Option>
                              <Option value={SubscriptionTypes.organization}>{SubscriptionTypes.organization_Text}</Option>
                            </Select>
                          </Form.Item>
                        </div>
                      </div>
                      <br />
                      <div style={{ marginBottom: '0px' }}>
                        <PayPalScriptProvider
                          //options={{ "client-id": "AR8Y4nT7eI0mHOjUdQO6Al4pJaFLVtcOq05KXa-nKtSrMmhbC1bMDXJ8tD_Fn9knE1TsNCytMvfCjdDt", "currency": "USD" }}

                        options={{ "client-id": "test", "currency": "USD" }}

                        >
                          <PayPalButtons
                            style={{ layout: 'vertical', color: 'blue' }}

                            createOrder={(data, actions) => {
                              return actions.order.create({
                                purchase_units: [
                                  {

                                    description: 'Swilab Demo Purchase',
                                    items: [

                                      {
                                        name: 'Demo Purchase',
                                        quantity: '1',
                                        unit_amount: {
                                          currency_code: 'USD',
                                          value: '2000.00'
                                        },
                                        tax: {
                                          currency_code: 'USD',
                                          value: '0.00'
                                        },
                                        description: 'Total',
                                        sku: 'SWI',

                                      }, 
                                    ],
                                    amount: {
                                      value: '2000.00',
                                      breakdown: {
                                        item_total: {
                                          currency_code: 'USD',
                                          value: '2000.00',
                                        },
                                        shipping: {
                                          currency_code: 'USD',
                                          value: '0.00'
                                        },
                                        handling: {
                                          currency_code: 'USD',
                                          value: '0.00'
                                        },
                                        tax_total: {
                                          currency_code: 'USD',
                                          value: '0.00'
                                        },
                                        insurance: {
                                          currency_code: 'USD',
                                          value: '0.00'
                                        },
                                        shipping_discount: {
                                          currency_code: 'USD',
                                          value: '0.00'
                                        },
                                        discount: {
                                          currency_code: 'USD',
                                          value: '0.00'
                                        },
                                      },
                                    },

                                  },

                                ],


                              });
                            }}

                           

                            onError={(err) => {
 
                              console.log(err);


                              return notification.open({
                                message: 'Oh No!',
                                description: 'Error'
                              });
                            }}

                            onCancel={() => {
                              return notification.open({
                                message: 'Payment Cancelled',
                                description:
                                  'Your order has been Cancelled.'
                              });
                            }}
                          />


                        </PayPalScriptProvider>
                      </div>

                    </Panel>

                    <Panel className="font-face-gm"
                      style={{ fontSize: '1.8em' }}
                      header={<div style={{ marginLeft: '25px', fontSize: '0.8em' }}>{L('Company Info')}</div>}
                      key={'companyInfo'} extra={

                        <Switch
                          style={{ float: 'right' }}
                          key={'sameAsTenantCompanyInfo'}
                          onClick={(e) => this.setState({ sameAsTenantCompanyInfo: e })}
                          size={'default'}
                          checked={this.state.sameAsTenantCompanyInfo}
                          checkedChildren={L('Same as Tenant')}
                          unCheckedChildren={L('Same as Tenant')} />

                      }>
                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>

                          <Tag className="font-face-gm" style={{
                            float: 'left',
                            paddingTop: '10px',
                            fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%',
                            height: '40px', resize: 'block',
                          }}
                            color={this.props.primaryColor}>{L('Name')}</Tag>

                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>

                          <Form.Item key={'name'} name={'name'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCompanyInfo} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Address #1')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'addressOne'} name={'addressOne'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCompanyInfo} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Address #2')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'addressTwo'} name={'addressTwo'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCompanyInfo} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('City')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'city'} name={'city'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCompanyInfo} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('State')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'state'} name={'state'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCompanyInfo} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Zip')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'zip'} name={'zip'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCompanyInfo} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Country')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'country'} name={'country'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCompanyInfo} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Phone')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'phone'} name={'phone'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCompanyInfo} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Tax ID')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'taxID'} name={'taxID'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCompanyInfo} />

                          </Form.Item>
                        </div>
                      </div>

                      <br /><br /><br /><br /><br /><br /><br /><br /> 
                      <br /><br /><br /><br /><br /><br /><br /><br /> 

                    </Panel>

                    <Panel className="font-face-gm"
                      style={{ fontSize: '1.8em' }}
                      header={<div style={{ marginLeft: '25px', fontSize: '0.8em' }}>{L('Primary Contact')}</div>}
                      key={'subscriberContact'} extra={

                        <Switch
                          style={{ float: 'right' }}
                          key={'sameAsTenantPrimaryContact'}
                          onClick={(e) => this.setState({ sameAsTenantPrimaryContact: e })}
                          size={'default'}
                          checked={this.state.sameAsTenantPrimaryContact}
                          checkedChildren={L('Same as Tenant')}
                          unCheckedChildren={L('Same as Tenant')} />

                      }>
                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Title')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'subscriberTitle'} name={'subscriberTitle'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantPrimaryContact} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>

                          <Tag className="font-face-gm" style={{
                            float: 'left',
                            paddingTop: '10px',
                            fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%',
                            height: '40px', resize: 'block',
                          }}
                            color={this.props.primaryColor}>{L('Name')}</Tag>

                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>

                          <Form.Item key={'subscriberFirstName'} name={'subscriberFirstName'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantPrimaryContact} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Surname')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'subscriberLastName'} name={'subscriberLastName'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantPrimaryContact} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Email')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'subscriberEmail'} name={'subscriberEmail'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantPrimaryContact} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Phone')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'subscriberPhone'} name={'subscriberPhone'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantPrimaryContact} />

                          </Form.Item>
                        </div>
                      </div>

                      
                      <br /><br /><br /><br /><br /><br /><br /><br />  
                    </Panel>

                    <Panel className="font-face-gm"
                      style={{ fontSize: '1.8em' }}
                      header={<div style={{ marginLeft: '25px', fontSize: '0.8em' }}>{L('Payment Info')}</div>}
                      key={'paymentsInfo'} extra={

                        <Switch
                          style={{ float: 'right' }}
                          key={'sameAsTenantPaymentInfo'}
                          onClick={(e) => this.setState({ sameAsTenantPaymentInfo: e })}
                          size={'default'}
                          checked={this.state.sameAsTenantPaymentInfo}
                          checkedChildren={L('Same as Tenant')}
                          unCheckedChildren={L('Same as Tenant')} />

                      }>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('PayPal ID')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'payPalID'} name={'payPalID'}  >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantPaymentInfo} />

                          </Form.Item>
                        </div>
                      </div>

                    </Panel>

                    <Panel className="font-face-gm"
                      style={{ fontSize: '1.8em' }}
                      header={<div style={{ marginLeft: '25px', fontSize: '0.8em' }}>{L('Email Connection')}</div>}
                      key={'communicationInfo'} extra={

                        <Switch
                          style={{ float: 'right' }}
                          key={'sameAsTenantCommunicationInfo'}
                          onClick={(e) => this.setState({ sameAsTenantCommunicationInfo: e })}
                          size={'default'}
                          checked={this.state.sameAsTenantCommunicationInfo}
                          checkedChildren={L('Same as Tenant')}
                          unCheckedChildren={L('Same as Tenant')} />

                      }>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Email')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'notificationEmail'} name={'notificationEmail'}  >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCommunicationInfo} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Pseudonym')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'notificationEmailPseudonym'} name={'notificationEmailPseudonym'} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCommunicationInfo} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Password')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'notificationPassword'} name={'notificationPassword'}  >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCommunicationInfo} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Server')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'notificationServer'} name={'notificationServer'}  >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCommunicationInfo} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Port')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'notificationServerPort'} name={'notificationServerPort'}  >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCommunicationInfo} />

                          </Form.Item>
                        </div>
                      </div>

                      <br /><br /><br /><br /><br /><br /><br /><br />  
                    </Panel>

                    <Panel className="font-face-gm"
                      style={{ fontSize: '1.8em' }}
                      header={<div style={{ marginLeft: '25px', fontSize: '0.8em' }}>{L('Theme')}</div>}
                      key={'themeInfo'} extra={

                        <Switch
                          style={{ float: 'right' }}
                          key={'customTheme'}
                          onClick={(e) => this.setState({ customTheme: e })}
                          size={'default'}
                          checked={this.state.customTheme}
                          checkedChildren={L('Custom Theme')}
                          unCheckedChildren={L('Custom Theme')} />

                      }>
                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('title')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'title'} name={'title'}  >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={!this.state.customTheme} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Color #1')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'primaryColor'} name={'primaryColor'} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={!this.state.customTheme} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Color #2')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'secondaryColor'} name={'secondaryColor'}  >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={!this.state.customTheme} />

                          </Form.Item>
                        </div>
                      </div>

                      
                      <br /><br /><br /> <br />
                    </Panel>

                    <Panel className="font-face-gm"
                      style={{ fontSize: '1.8em' }}
                      header={<div style={{ marginLeft: '25px', fontSize: '0.8em', float: 'left' }}>{L('Default Administrator')}</div>}
                      key={'adminInfo'} extra={

                        <Switch
                          style={{ float: 'right' }}
                          key={'sameAsSubscriber'}
                          onClick={(e) => this.setState({ adminSameAsContact: e })}
                          size={'default'}
                          checked={this.state.adminSameAsContact}
                          checkedChildren={L('Same As Contact')}
                          unCheckedChildren={L('Same As Contact')} />

                      }>


                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Title')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'adminTitle'} name={'adminTitle'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.adminSameAsContact} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>

                          <Tag className="font-face-gm" style={{
                            float: 'left',
                            paddingTop: '10px',
                            fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%',
                            height: '40px', resize: 'block',
                          }}
                            color={this.props.primaryColor}>{L('Name')}</Tag>

                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>

                          <Form.Item key={'adminName'} name={'adminFirstName'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.adminSameAsContact} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Surname')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'adminLastName'} name={'adminLastName'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.adminSameAsContact} />

                          </Form.Item>
                        </div>
                      </div>


                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Email')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'adminEmail'} name={'adminEmail'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.adminSameAsContact} />

                          </Form.Item>
                        </div>
                      </div>


                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('Phone')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'sadminPhone'} name={'adminPhone'} rules={[]} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.adminSameAsContact} />

                          </Form.Item>
                        </div>
                      </div>


                      <br /><br /><br /><br /><br /><br /><br /><br />  
                    </Panel>

                    <Panel className="font-face-gm"
                      style={{ fontSize: '1.8em' }}
                      header={<div style={{ marginLeft: '25px', fontSize: '0.8em' }}>{L('Database')}</div>}
                      key={'dbInfo'} extra={

                        <Switch
                          style={{ float: 'right' }}
                          key={'isCloudDatabase'}
                          onClick={(e) => this.setState({ isCloudDatabase: e })}
                          size={'default'}
                          checked={this.state.isCloudDatabase}
                          checkedChildren={L('Cloud Database')}
                          unCheckedChildren={L('Cloud Database')} />

                      }>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('MSSQL')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'connectionString'} name={'connectionString'}  >

                            <Input  className="font-face-gm" placeholder={L('Connection String...')} size="large" disabled={this.state.isCloudDatabase} />

                          </Form.Item>
                        </div>
                      </div>

                    </Panel>

                    <Panel className="font-face-gm"
                      style={{ fontSize: '1.8em' }}
                      header={<div style={{ marginLeft: '25px', fontSize: '0.8em' }}>{L('FTP Server')}</div>}
                      key={'ftpInfo'} extra={

                        <Switch
                          style={{ float: 'right' }}
                          key={'isCloudFTP'}
                          onClick={(e) => this.setState({ isCloudFTP: e })}
                          size={'default'}
                          checked={this.state.isCloudFTP}
                          checkedChildren={L('Cloud File Storage')}
                          unCheckedChildren={L('Cloud File Storage')} />

                      }>
                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('FTP URL')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'ftpurl'} name={'ftpurl'} >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.isCloudFTP} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('FTP User')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'ftpUser'} name={'ftpUser'}   >

                            <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.isCloudFTP} />

                          </Form.Item>
                        </div>
                      </div>

                      <div style={{ marginBottom: '60px' }}>
                        <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                          <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                            color={this.props.primaryColor}>{L('FTP Pass')}</Tag>
                        </div>
                        <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                          <Form.Item key={'ftpPassword'} name={'ftpPassword'}  >

                            <Input  className="font-face-gm" type={'password'} placeholder={L('...')} size="large" disabled={this.state.isCloudFTP} />

                          </Form.Item>
                        </div>
                      </div>

                      
                      <br /><br /><br /><br />
                    </Panel>

                  </Collapse>


                  <div style={{ width: '100%', float: 'right', padding: '0', marginTop: '35px', marginBottom: '-5px' }}>

                    <Form.Item style={{ margin: '0', float: 'right' }}>
                      <Button style={{ background: this.props.secondaryColor, borderColor: 'white' }} loading={this.props.loading} size='large' type="primary" shape='round' htmlType="submit" className="font-face-gm">
                        {L('Submit')}
                      </Button>
                    </Form.Item>
                    <Form.Item style={{ margin: '0', float: 'right', marginRight: '10px' }}>
                      <Button loading={this.props.loading} size='large' type="default" shape='round' onClick={this.handleCancel} className="font-face-gm">
                        {L('Cancel')}
                      </Button>
                    </Form.Item>

                  </div>
                </Card>


              </Form>


              :
              <div style={{ height: '150px', margin: 'auto', marginTop: '40px', width: '95%', textAlign: 'center' }}>

                <Skeleton active />

              </div>}




          </Modal>
        </div>

        <div>
          <Drawer
            onClose={this.handleCancel}
            closeIcon={false}
            open={isMobile === true ? (visible === true ? true : false) : false}

            width={isMobile === true ? (window.innerWidth * 0.9) : 420}  >
            <Divider className="font-face-gm" type="horizontal" style={{ fontSize: '2.3em', marginBottom: '0px', }} >
              {L('Create Account')}
            </Divider>
            <Row style={{ marginBottom: '0px' }}>
              <Progress
                strokeColor={{
                  from: this.props.secondaryColor,
                  to: this.props.primaryColor,
                }}
                showInfo={false}
                percent={100}
                status="normal"
                size={'small'}
              />
            </Row>

            <Form ref={this.formRef} style={{ marginTop: '0px', marginBottom: '100px' }} name={'createTenantForm'} onFinish={(e) => this.handleFinish(e)}>


              <Divider className="font-face-gm" orientation='left' style={{ marginTop: '10px', fontSize: '1.1em' }} >{L('Profile Image')}</Divider>

              <Row style={{ textAlign: 'center', margin: 'auto', marginTop: '20px' }}>

                <div style={{ margin: 'auto' }}>
                  <Form.Item name={'profileImageURL'} key={'profileImageURL'}  >
                    <Upload

                      action={(file) =>
                        this.uploadImage(file)
                      }
                      listType="picture-card"
                      showUploadList={false}
                      beforeUpload={this.beforeUpload}
                      className="avatar-uploader"
                      onChange={this.handleChange}
                      multiple={false}
                      key={'profileImageURL'} >
                      {this.state.imageUrl !== '' ? <img src={this.state.imageUrl} alt="avatar" style={{ width: '100%' }} /> : uploadWrapper}
                    </Upload></Form.Item>
                </div>
              </Row>
              <Card bordered={false} style={{ padding: '0px', marginTop: '0px', marginBottom: '-25px' }}>

                <Row>

                  <div>
                    <div style={{ float: 'left', width: '30%', marginRight: '12px' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: '1.2em', textAlign: 'center', width: '100%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Name')}</Tag>
                    </div>
                    <div style={{ float: 'left', width: '65%' }}>
                      <Form.Item className="font-face-gm" name={'name'} key={'name'}>
                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.props.loading} />
                      </Form.Item>
                    </div>
                  </div>
                  <div>
                    <div style={{ float: 'left', width: '30%', marginRight: '12px' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: '1.2em', textAlign: 'center', width: '100%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Title')}</Tag>
                    </div>
                    <div style={{ float: 'left', width: '65%' }}>
                      <Form.Item className="font-face-gm" name={'title'} key={'title'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.props.loading} />

                      </Form.Item>
                    </div>

                  </div>

                </Row>

              </Card>

              <Collapse accordion={true}
                key={'Name'}
                bordered={true}
                defaultActiveKey={'companyInfo'}
                style={{
                  fontSize: '0.8em',
                  backgroundColor: 'transparent',
                  marginBottom: '0px',
                  marginTop: '0px',
                  border: 'none',
                  boxShadow: 'none'
                }}>

                <Panel className="font-face-gm"
                  style={{ fontSize: '1.8em' }}
                  header={<div style={{ marginLeft: '25px', fontSize: '0.8em' }}>{L('Subscription Type')}</div>}
                  key={'sunscriptionInfo'} extra={

                    <Switch
                      style={{ float: 'right' }}
                      key={'isDemoAccount'}
                      onClick={(e) => this.setState({ isDemoAccount: e })}
                      size={'default'}
                      checked={this.state.isDemoAccount}
                      checkedChildren={L('Demo Account')}
                      unCheckedChildren={L('Demo Account')} />

                  }>


                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Plan')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item initialValue={SubscriptionTypes.businessBasic} style={{ margin: '0' }}
                        name={'type'} key={'type'} rules={[]}  >
                        <Select size="large" key={'subscriptionType'} style={{ width: '100%' }}
                          defaultValue={SubscriptionTypes.businessBasic} disabled={this.state.isDemoAccount}>
                          <Option value={SubscriptionTypes.businessBasic}>{SubscriptionTypes.businessBasic_Text}</Option>
                          <Option value={SubscriptionTypes.businessStandard}>{SubscriptionTypes.businessStandard_Text}</Option>
                          <Option value={SubscriptionTypes.enterpriseAdvanced}>{SubscriptionTypes.enterpriseAdvanced_Text}</Option>
                          <Option value={SubscriptionTypes.enterpriseUnlimited}>{SubscriptionTypes.enterpriseUnlimited_Text}</Option>
                          <Option value={SubscriptionTypes.organization}>{SubscriptionTypes.organization_Text}</Option>
                        </Select>
                      </Form.Item>
                    </div>
                  </div>


                </Panel>

                <Panel className="font-face-gm"
                  style={{ fontSize: '1.8em' }}
                  header={<div style={{ marginLeft: '25px', fontSize: '0.8em' }}>{L('Company Info')}</div>}
                  key={'companyInfo'} extra={

                    <Switch
                      style={{ float: 'right' }}
                      key={'sameAsTenantCompanyInfo'}
                      onClick={(e) => this.setState({ sameAsTenantCompanyInfo: e })}
                      size={'default'}
                      checked={this.state.sameAsTenantCompanyInfo}
                      checkedChildren={L('Same as Tenant')}
                      unCheckedChildren={L('Same as Tenant')} />

                  }>
                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>

                      <Tag className="font-face-gm" style={{
                        float: 'left',
                        paddingTop: '10px',
                        fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%',
                        height: '40px', resize: 'block',
                      }}
                        color={this.props.primaryColor}>{L('Name')}</Tag>

                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>

                      <Form.Item key={'name'} name={'name'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCompanyInfo} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Address #1')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'addressOne'} name={'addressOne'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCompanyInfo} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Address #2')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'addressTwo'} name={'addressTwo'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCompanyInfo} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('City')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'city'} name={'city'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCompanyInfo} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('State')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'state'} name={'state'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCompanyInfo} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Zip')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'zip'} name={'zip'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCompanyInfo} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Country')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'country'} name={'country'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCompanyInfo} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Phone')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'phone'} name={'phone'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCompanyInfo} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Tax ID')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'taxID'} name={'taxID'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCompanyInfo} />

                      </Form.Item>
                    </div>
                  </div>

                </Panel>

                <Panel className="font-face-gm"
                  style={{ fontSize: '1.8em' }}
                  header={<div style={{ marginLeft: '25px', fontSize: '0.8em' }}>{L('Primary Contact')}</div>}
                  key={'subscriberContact'} extra={

                    <Switch
                      style={{ float: 'right' }}
                      key={'sameAsTenantPrimaryContact'}
                      onClick={(e) => this.setState({ sameAsTenantPrimaryContact: e })}
                      size={'default'}
                      checked={this.state.sameAsTenantPrimaryContact}
                      checkedChildren={L('Same as Tenant')}
                      unCheckedChildren={L('Same as Tenant')} />

                  }>
                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Title')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'subscriberTitle'} name={'subscriberTitle'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantPrimaryContact} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>

                      <Tag className="font-face-gm" style={{
                        float: 'left',
                        paddingTop: '10px',
                        fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%',
                        height: '40px', resize: 'block',
                      }}
                        color={this.props.primaryColor}>{L('Name')}</Tag>

                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>

                      <Form.Item key={'subscriberFirstName'} name={'subscriberFirstName'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantPrimaryContact} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Surname')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'subscriberLastName'} name={'subscriberLastName'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantPrimaryContact} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Email')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'subscriberEmail'} name={'subscriberEmail'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantPrimaryContact} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Phone')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'subscriberPhone'} name={'subscriberPhone'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantPrimaryContact} />

                      </Form.Item>
                    </div>
                  </div>
                </Panel>

                <Panel className="font-face-gm"
                  style={{ fontSize: '1.8em' }}
                  header={<div style={{ marginLeft: '25px', fontSize: '0.8em' }}>{L('Payment Info')}</div>}
                  key={'paymentsInfo'} extra={

                    <Switch
                      style={{ float: 'right' }}
                      key={'sameAsTenantPaymentInfo'}
                      onClick={(e) => this.setState({ sameAsTenantPaymentInfo: e })}
                      size={'default'}
                      checked={this.state.sameAsTenantPaymentInfo}
                      checkedChildren={L('Same as Tenant')}
                      unCheckedChildren={L('Same as Tenant')} />

                  }>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('PayPal ID')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'payPalID'} name={'payPalID'}  >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantPaymentInfo} />

                      </Form.Item>
                    </div>
                  </div>

                </Panel>

                <Panel className="font-face-gm"
                  style={{ fontSize: '1.8em' }}
                  header={<div style={{ marginLeft: '25px', fontSize: '0.8em' }}>{L('Email Connection')}</div>}
                  key={'communicationInfo'} extra={

                    <Switch
                      style={{ float: 'right' }}
                      key={'sameAsTenantCommunicationInfo'}
                      onClick={(e) => this.setState({ sameAsTenantCommunicationInfo: e })}
                      size={'default'}
                      checked={this.state.sameAsTenantCommunicationInfo}
                      checkedChildren={L('Same as Tenant')}
                      unCheckedChildren={L('Same as Tenant')} />

                  }>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Email')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'notificationEmail'} name={'notificationEmail'}  >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCommunicationInfo} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Pseudonym')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'notificationEmailPseudonym'} name={'notificationEmailPseudonym'} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCommunicationInfo} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Password')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'notificationPassword'} name={'notificationPassword'}  >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCommunicationInfo} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Server')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'notificationServer'} name={'notificationServer'}  >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCommunicationInfo} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Port')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'notificationServerPort'} name={'notificationServerPort'}  >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.sameAsTenantCommunicationInfo} />

                      </Form.Item>
                    </div>
                  </div>

                </Panel>

                <Panel className="font-face-gm"
                  style={{ fontSize: '1.8em' }}
                  header={<div style={{ marginLeft: '25px', fontSize: '0.8em' }}>{L('Theme')}</div>}
                  key={'themeInfo'} extra={

                    <Switch
                      style={{ float: 'right' }}
                      key={'customTheme'}
                      onClick={(e) => this.setState({ customTheme: e })}
                      size={'default'}
                      checked={this.state.customTheme}
                      checkedChildren={L('Custom Theme')}
                      unCheckedChildren={L('Custom Theme')} />

                  }>
                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('title')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'title'} name={'title'}  >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={!this.state.customTheme} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Color #1')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'primaryColor'} name={'primaryColor'} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={!this.state.customTheme} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Color #2')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'secondaryColor'} name={'secondaryColor'}  >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={!this.state.customTheme} />

                      </Form.Item>
                    </div>
                  </div>
                </Panel>

                <Panel className="font-face-gm"
                  style={{ fontSize: '1.8em' }}
                  header={<div style={{ marginLeft: '25px', fontSize: '0.8em', float: 'left' }}>{L('Default Administrator')}</div>}
                  key={'adminInfo'} extra={

                    <Switch
                      style={{ float: 'right' }}
                      key={'sameAsSubscriber'}
                      onClick={(e) => this.setState({ adminSameAsContact: e })}
                      size={'default'}
                      checked={this.state.adminSameAsContact}
                      checkedChildren={L('Same As Contact')}
                      unCheckedChildren={L('Same As Contact')} />

                  }>


                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Title')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'adminTitle'} name={'adminTitle'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.adminSameAsContact} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>

                      <Tag className="font-face-gm" style={{
                        float: 'left',
                        paddingTop: '10px',
                        fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%',
                        height: '40px', resize: 'block',
                      }}
                        color={this.props.primaryColor}>{L('Name')}</Tag>

                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>

                      <Form.Item key={'adminName'} name={'adminFirstName'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.adminSameAsContact} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Surname')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'adminLastName'} name={'adminLastName'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.adminSameAsContact} />

                      </Form.Item>
                    </div>
                  </div>


                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Email')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'adminEmail'} name={'adminEmail'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.adminSameAsContact} />

                      </Form.Item>
                    </div>
                  </div>


                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('Phone')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'sadminPhone'} name={'adminPhone'} rules={[]} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.adminSameAsContact} />

                      </Form.Item>
                    </div>
                  </div>


                </Panel>

                <Panel className="font-face-gm"
                  style={{ fontSize: '1.8em' }}
                  header={<div style={{ marginLeft: '25px', fontSize: '0.8em' }}>{L('Database')}</div>}
                  key={'dbInfo'} extra={

                    <Switch
                      style={{ float: 'right' }}
                      key={'isCloudDatabase'}
                      onClick={(e) => this.setState({ isCloudDatabase: e })}
                      size={'default'}
                      checked={this.state.isCloudDatabase}
                      checkedChildren={L('Cloud Database')}
                      unCheckedChildren={L('Cloud Database')} />

                  }>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('MSSQL')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'connectionString'} name={'connectionString'}  >

                        <Input  className="font-face-gm" placeholder={L('Connection String...')} size="large" disabled={this.state.isCloudDatabase} />

                      </Form.Item>
                    </div>
                  </div>

                </Panel>

                <Panel className="font-face-gm"
                  style={{ fontSize: '1.8em' }}
                  header={<div style={{ marginLeft: '25px', fontSize: '0.8em' }}>{L('FTP Server')}</div>}
                  key={'ftpInfo'} extra={

                    <Switch
                      style={{ float: 'right' }}
                      key={'isCloudFTP'}
                      onClick={(e) => this.setState({ isCloudFTP: e })}
                      size={'default'}
                      checked={this.state.isCloudFTP}
                      checkedChildren={L('Cloud File Storage')}
                      unCheckedChildren={L('Cloud File Storage')} />

                  }>
                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('FTP URL')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'ftpurl'} name={'ftpurl'} >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.isCloudFTP} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('FTP User')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'ftpUser'} name={'ftpUser'}   >

                        <Input  className="font-face-gm" placeholder={L('...')} size="large" disabled={this.state.isCloudFTP} />

                      </Form.Item>
                    </div>
                  </div>

                  <div style={{ marginBottom: '60px' }}>
                    <div style={{ width: '25%', float: 'left', padding: '0', margin: '0' }}>
                      <Tag className="font-face-gm" style={{ float: 'left', paddingTop: '10px', fontSize: this.state.fieldNameSize, textAlign: 'center', width: '90%', height: '40px', resize: 'block' }}
                        color={this.props.primaryColor}>{L('FTP Pass')}</Tag>
                    </div>
                    <div style={{ width: '75%', float: 'left', padding: '0', margin: '0' }}>
                      <Form.Item key={'ftpPassword'} name={'ftpPassword'}  >

                        <Input  className="font-face-gm" type={'password'} placeholder={L('...')} size="large" disabled={this.state.isCloudFTP} />

                      </Form.Item>
                    </div>
                  </div>
                </Panel>
              </Collapse>




              <div style={{ width: '100%', float: 'right', padding: '0', marginTop: '30px', }}>

                <Form.Item style={{ margin: '0', float: 'right' }}>
                  <Button style={{ background: this.props.secondaryColor, borderColor: 'white' }} loading={this.props.loading} size='large' type="primary" shape='round' htmlType="submit" className="font-face-gm">
                    {L('Submit')}
                  </Button>
                </Form.Item>
                <Form.Item style={{ margin: '0', float: 'right', marginRight: '10px' }}>
                  <Button loading={this.props.loading} size='large' type="default" shape='round' onClick={this.handleCancel} className="font-face-gm">
                    {L('Cancel')}
                  </Button>
                </Form.Item>

              </div>

            </Form>

          </Drawer>
        </div>

      </div>

    );
  }
}

export default CreateTenant;
